import React from 'react';
import axios from 'axios';
import get from 'lodash/get';

import { Box } from '@origin-digital/layout';
import { Text, LinkText, Spinner, AlertBox } from '@origin-digital/style-guide';
import { LaunchIcon } from '@origin-digital/icon';

import { Container, Content, Actions, ContentText, Heading, LoadingOverlay } from '../common';
import { Checkbox, CheckboxContainer } from '../common/Checkbox';
import { IWizardStepProps, WizardContext } from '../../Wizard';
import { ElectricityIcon, NaturalGasIcon, SolarIcon } from '../icons';
import { isEligible, isSolarAvailable } from '../utils';

interface IEnergyTypeProps extends IWizardStepProps {
  plansApiEndPoint: string;
}

export class EnergyType extends React.Component<IEnergyTypeProps> {
  static contextType = WizardContext;
  public state = { loading: false };

  handleFuelToggle = (event) => {
    const { name, checked } = event.target;
    const isElecUnchecked = name === 'electricity' && !checked;
  
    const answers: any = {
      [name]: checked
    };

    if (isElecUnchecked) {
      answers.solar = false;
    }

    this.context.saveAnswers(answers);
  };

  handleSolarToggle = (event) => {
    const { checked } = event.target;
    const answers = {
      solar: checked,
    } as any;
    if (checked) {
      answers.electricity = checked;
    }

    const { solarAvailable, usePostcode } = this.context.answers;
    if (!usePostcode && solarAvailable === undefined) {
      this.checkSolarAvailability();
    } else {
      this.context.saveAnswers(answers);
    }
  };

  checkSolarAvailability = () => {
    const { plansApiEndPoint } = this.props;
    const { postcode, address, electricity } = this.context.answers;
    this.setState({ loading: true });

    return axios
      .get(plansApiEndPoint, {
        params: {
          addressId: get(address, 'sapAddressId'),
          connectionScenario: 'PROS_SWT',
          customerType: 'residential',
          fuelType: 'dual',
          hasSolarPanels: true,
          postcode,
          searchByPostcode: false,
        },
      })
      .then((response) => {
        const { plans, fuelEligibilityStatus } = response.data.data;
        const solarAvailable = isSolarAvailable(plans);
        const answers = {
          fuelEligibilityStatus,
          plans,
          solarAvailable,
          solar: solarAvailable,
          electricity: !electricity ? solarAvailable : electricity
        };

        if (!solarAvailable) {
          this.setState({
            showSolarError: true,
          });
        }
        this.context.saveAnswers(answers);
      })
      .finally(() => {
        this.setState({ loading: false });
      });
  };

  render() {
    const { loading } = this.state;
    const hasElec = isEligible(get(this.context.answers, 'fuelEligibilityStatus.electricity'));
    const hasGas = isEligible(get(this.context.answers, 'fuelEligibilityStatus.gas'));
    const solarDisabled = !hasElec || this.context.answers.solarAvailable === false;
    return (
      <Container>
        {loading && (
          <LoadingOverlay>
            <Spinner size="lg" />
          </LoadingOverlay>
        )}
        <Content>
          {this.state.showSolarError && (
            <AlertBox
              type="info"
              message="Sorry, we can't find your solar details. Our results suggest you don't have solar at this address."
            />
          )}
          <Heading>Select the energy type(s) you need</Heading>
          <ContentText>
            Based on your location, we’ve got the following options available. You can select more
            than one.
          </ContentText>

          <Box>
            <Box marginBottom={4}>
              <CheckboxContainer>
                <Checkbox
                  label="Electricity"
                  name="electricity"
                  checked={this.context.answers.electricity}
                  onChange={this.handleFuelToggle}
                  disabled={!hasElec}
                >
                  <ElectricityIcon />
                </Checkbox>
                <Checkbox
                  label="Natural gas"
                  name="gas"
                  checked={this.context.answers.gas}
                  onChange={this.handleFuelToggle}
                  disabled={!hasGas}
                >
                  <NaturalGasIcon />
                </Checkbox>
                <Checkbox
                  label={
                    <>
                      Electricity for solar
                      {solarDisabled && <Text size="xs">(Not available at your location)</Text>}
                    </>
                  }
                  name="solar"
                  checked={this.context.answers.solar}
                  onChange={this.handleSolarToggle}
                  disabled={solarDisabled}
                >
                  <SolarIcon />
                </Checkbox>
              </CheckboxContainer>
            </Box>
            <Text>
              <LinkText
                href="/for-home/lpg.html#getQuote"
                text={
                  <>
                    Looking for LPG gas bottles?
                    <span style={{ marginLeft: '4px' }}>
                      <LaunchIcon size={16} color="grey" />
                    </span>
                  </>
                }
                target="_blank"
              />
            </Text>
          </Box>
          <Actions {...this.props} />
        </Content>
      </Container>
    );
  }
}
